import {Fragment} from 'react';
import {Breadcrumb, HStack, IconButton} from '@chakra-ui/react';
import { ArrowUp, ChevronRight } from 'lucide-react';
import {Link as RouterLink, useNavigate} from 'react-router';
import {useFileMeta} from '../drive/queries';
import {parseFolderName} from '../domain/naming';
import { NoexportBadge } from './NoexportBadge';

const MAX_CRUMBS = 6;
const MAX_CRUMBS_MOBILE = 4;

export type BreadcrumbSegment =
    | { label: string; to?: string }
    | { resolveId: string; to?: string };

function isResolvedSegment(segment: BreadcrumbSegment): segment is { resolveId: string; to?: string } {
    return 'resolveId' in segment;
}

/** A crumb's link (or the current page's name) and, for a noexport folder, its badge. */
function CrumbContent({segment, isLast}: { segment: BreadcrumbSegment; isLast: boolean }) {
    // A resolved segment's folder name is read once, for both its label and its badge.
    const metaQuery = useFileMeta(isResolvedSegment(segment) ? segment.resolveId : undefined);
    const folderName = metaQuery.data?.name;
    const parsedName = folderName ? parseFolderName(folderName) : undefined;
    const label = isResolvedSegment(segment) ? (parsedName?.displayName ?? '…') : segment.label;
    return <>
        {isLast
            ? <Breadcrumb.CurrentLink margin={1} color="inherit">{label}</Breadcrumb.CurrentLink>
            : segment.to
                ? <Breadcrumb.Link asChild margin={1} color="inherit" _hover={{textDecoration: 'underline', color: 'inherit'}}>
                    <RouterLink to={segment.to}>{label}</RouterLink>
                </Breadcrumb.Link>
                : <Breadcrumb.Link margin={1} color="inherit" cursor="default">{label}</Breadcrumb.Link>
        }
        {parsedName?.noexport && <NoexportBadge ml="1" shortened/>}
    </>;
}

interface DriveBreadcrumbProps {
    path: BreadcrumbSegment[];
    backTo?: string;
}

type Display = 'none' | 'flex' | { base: 'none' | 'flex'; sm: 'none' | 'flex' } | undefined;

export function DriveBreadcrumb({path, backTo}: DriveBreadcrumbProps) {
    const navigate = useNavigate();
    const shouldCollapseMiddle = path.length > MAX_CRUMBS_MOBILE;
    const collapseOnDesktop = path.length > MAX_CRUMBS;  // if it's really long, collapse the middle even on desktop

    // Each crumb carries its separator, so a hidden crumb hides its separator too.
    function renderCrumb(key: string | number, display: Display, content: React.ReactNode, isLast: boolean) {
        return <Fragment key={key}>
            <Breadcrumb.Item display={display}>{content}</Breadcrumb.Item>
            {!isLast &&
                <Breadcrumb.Separator display={display} color="inherit">
                    <ChevronRight size={16} color="gray"/>
                </Breadcrumb.Separator>}
        </Fragment>;
    }

    function renderSegment(segment: BreadcrumbSegment, index: number, hideAlways = false, hideOnMobile = false) {
        const isLast = index === path.length - 1;
        const display: Display = hideAlways ? 'none' : hideOnMobile ? {base: 'none', sm: 'flex'} : undefined;
        return renderCrumb(index, display, <CrumbContent segment={segment} isLast={isLast}/>, isLast);
    }

    const breadcrumbItems = shouldCollapseMiddle
        ? [
            ...path.slice(0, 2).map((segment, index) => renderSegment(segment, index)),
            renderCrumb('ellipsis', collapseOnDesktop ? 'flex' : {base: 'flex', sm: 'none'},
                <Breadcrumb.Link margin={1} color="inherit" cursor="default">...</Breadcrumb.Link>, false),
            ...path.slice(2).map((segment, index) => renderSegment(
                segment,
                index + 2,
                index < path.length - MAX_CRUMBS,
                index < path.length - MAX_CRUMBS_MOBILE,
            )),
        ]
        : path.map((segment, index) => renderSegment(segment, index));

    return <HStack justifyContent="start" gap={{base: 2, md: 4}}>
        <IconButton
            aria-label="Vissza"
            variant="subtle"
            onClick={() => backTo ? navigate(backTo) : navigate(-1)}
        >
            <ArrowUp size={16} />
        </IconButton>
        <Breadcrumb.Root>
            <Breadcrumb.List gap={{base: '3px', md: '6px'}} color="inherit" textStyle="md">
                {breadcrumbItems}
            </Breadcrumb.List>
        </Breadcrumb.Root>
    </HStack>;
}
